import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, Users, Radio, Gamepad2, ShieldCheck, Eye } from "lucide-react";
import { api } from "@/lib/api";

/**
 * GlobalSearch — Landing-page omnibox.
 *
 * Live-debounced search across users, live streamers and games.
 * Results render in a neon dropdown grouped by category. Click to navigate.
 */
export default function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null = idle, {} = loaded
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Debounced live search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const query = q.trim();
    if (!query) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const rid = ++reqIdRef.current;
    debounceRef.current = setTimeout(() => {
      api
        .get("/search", { params: { q: query, limit: 6 } })
        .then((r) => {
          if (rid !== reqIdRef.current) return; // stale
          setResults(r.data);
          setLoading(false);
        })
        .catch(() => {
          if (rid !== reqIdRef.current) return;
          setResults({ users: [], streamers: [], games: [] });
          setLoading(false);
        });
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Flatten results for keyboard nav
  const flat = React.useMemo(() => {
    if (!results) return [];
    const list = [];
    (results.streamers || []).forEach((s) => list.push({ type: "streamer", data: s }));
    (results.users || []).forEach((u) => list.push({ type: "user", data: u }));
    (results.games || []).forEach((g) => list.push({ type: "game", data: g }));
    return list;
  }, [results]);

  useEffect(() => { setActiveIdx(-1); }, [q, results]);

  const goTo = (item) => {
    if (!item) return;
    setOpen(false);
    setQ("");
    if (item.type === "streamer") nav(`/c/${item.data.username}/live`);
    else if (item.type === "user") nav(`/u/${item.data.username}`);
    else if (item.type === "game") nav("/games");
  };

  const onKey = (e) => {
    if (!open || flat.length === 0) {
      if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) goTo(flat[activeIdx]);
      else if (flat[0]) goTo(flat[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && q.trim().length > 0;
  const total = flat.length;

  return (
    <div ref={wrapRef} className="relative w-full max-w-2xl" data-testid="global-search-wrap">
      <div className="relative">
        <SearchIcon
          className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: "var(--sc-cyan)" }}
        />
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search friends, streamers, games..."
          data-testid="global-search-input"
          className="w-full pl-11 pr-11 py-3.5 bg-[#0F0F14]/90 border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none focus:ring-1 focus:ring-[color:var(--sc-cyan)]/40 rounded-lg sc-mono text-sm tracking-wide text-zinc-100 placeholder:text-zinc-600 transition-colors backdrop-blur-md"
          autoComplete="off"
          spellCheck={false}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); inputRef.current?.focus(); }}
            data-testid="global-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-zinc-500 hover:text-white hover:bg-white/10 transition"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 mt-2 z-50 term-card cyan overflow-hidden shadow-2xl"
          style={{ background: "rgba(10,10,12,0.98)", backdropFilter: "blur(16px)" }}
          data-testid="global-search-dropdown"
        >
          {/* Loading state */}
          {loading && (
            <div className="p-6 text-center" data-testid="global-search-loading">
              <div className="sc-mono text-[10px] tracking-widest text-zinc-500 animate-pulse">
                ◈ SCANNING GRID...
              </div>
            </div>
          )}

          {/* No results */}
          {!loading && results && total === 0 && (
            <div className="p-8 text-center" data-testid="global-search-empty">
              <SearchIcon className="w-6 h-6 mx-auto mb-3 text-zinc-600" />
              <div className="sc-heading text-sm font-bold text-zinc-400">
                No signals for &quot;{q}&quot;
              </div>
              <div className="text-xs text-zinc-600 mt-1">
                Try a username, stream title or game name.
              </div>
            </div>
          )}

          {/* Streamers section (live) */}
          {!loading && results?.streamers?.length > 0 && (
            <SearchSection
              icon={Radio}
              label="LIVE STREAMERS"
              color="pink"
              testid="global-search-streamers"
            >
              {results.streamers.map((s, i) => {
                const idx = i;
                return (
                  <ResultRow
                    key={`s-${s.username}`}
                    active={activeIdx === idx}
                    onClick={() => goTo({ type: "streamer", data: s })}
                    testid={`global-search-streamer-${s.username}`}
                  >
                    <Avatar url={s.avatar_url} name={s.username} color="pink" live />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="sc-heading text-sm font-bold truncate">
                          {s.title || s.username}
                        </div>
                        {s.is_verified_streamer && (
                          <ShieldCheck className="w-3 h-3 flex-shrink-0" style={{ color: "var(--sc-volt)" }} />
                        )}
                      </div>
                      <div className="sc-mono text-[10px] text-zinc-500 truncate">
                        @{s.username} {s.category ? `· ${s.category}` : ""}
                      </div>
                    </div>
                    <div className="chip-neon pink flex-shrink-0">
                      <Eye className="w-2.5 h-2.5" /> {s.viewer_count ?? 0}
                    </div>
                  </ResultRow>
                );
              })}
            </SearchSection>
          )}

          {/* Users section */}
          {!loading && results?.users?.length > 0 && (
            <SearchSection
              icon={Users}
              label="PEOPLE"
              color="cyan"
              testid="global-search-users"
              offset={results.streamers?.length || 0}
            >
              {results.users.map((u, i) => {
                const idx = (results.streamers?.length || 0) + i;
                return (
                  <ResultRow
                    key={`u-${u.username}`}
                    active={activeIdx === idx}
                    onClick={() => goTo({ type: "user", data: u })}
                    testid={`global-search-user-${u.username}`}
                  >
                    <Avatar url={u.avatar_url} name={u.username} color="cyan" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="sc-heading text-sm font-bold truncate">
                          {u.display_name || u.username}
                        </div>
                        {u.is_verified_streamer && (
                          <ShieldCheck className="w-3 h-3 flex-shrink-0" style={{ color: "var(--sc-volt)" }} />
                        )}
                      </div>
                      <div className="sc-mono text-[10px] text-zinc-500 truncate">
                        @{u.username} {u.user_code ? `· ${u.user_code}` : ""}
                      </div>
                    </div>
                  </ResultRow>
                );
              })}
            </SearchSection>
          )}

          {/* Games section */}
          {!loading && results?.games?.length > 0 && (
            <SearchSection
              icon={Gamepad2}
              label="GAMES"
              color="volt"
              testid="global-search-games"
              offset={(results.streamers?.length || 0) + (results.users?.length || 0)}
            >
              {results.games.map((g, i) => {
                const idx = (results.streamers?.length || 0) + (results.users?.length || 0) + i;
                return (
                  <ResultRow
                    key={`g-${g.slug}`}
                    active={activeIdx === idx}
                    onClick={() => goTo({ type: "game", data: g })}
                    testid={`global-search-game-${g.slug}`}
                  >
                    <div className="w-9 h-9 flex-shrink-0 rounded border-2 border-[color:var(--sc-volt)] flex items-center justify-center bg-[#0A0A0C]">
                      <Gamepad2 className="w-4 h-4" style={{ color: "var(--sc-volt)" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="sc-heading text-sm font-bold truncate">{g.name}</div>
                      <div className="sc-mono text-[10px] text-zinc-500 truncate">
                        {g.players} · {g.category}
                      </div>
                    </div>
                    {g.available ? (
                      <span className="chip-neon volt flex-shrink-0">PLAY</span>
                    ) : (
                      <span className="sc-mono text-[9px] tracking-widest text-zinc-600 flex-shrink-0">
                        SOON
                      </span>
                    )}
                  </ResultRow>
                );
              })}
            </SearchSection>
          )}

          {/* Footer hint */}
          {!loading && total > 0 && (
            <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between sc-mono text-[9px] tracking-widest text-zinc-600">
              <span>↑ ↓ NAVIGATE · ↵ SELECT · ESC CLOSE</span>
              <span>{total} RESULT{total !== 1 ? "S" : ""}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SearchSection({ icon: Icon, label, color, testid, children }) {
  return (
    <div className="py-2" data-testid={testid}>
      <div className="px-4 py-1.5 flex items-center gap-2 border-b border-white/5">
        <Icon className="w-3 h-3" style={{ color: `var(--sc-${color})` }} />
        <span
          className="sc-label text-[9px] tracking-widest"
          style={{ color: `var(--sc-${color})` }}
        >
          {label}
        </span>
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}

function ResultRow({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? "bg-white/10" : "hover:bg-white/5"
      }`}
    >
      {children}
    </button>
  );
}

function Avatar({ url, name, color = "cyan", live = false }) {
  const initials = (name || "?").slice(0, 2).toUpperCase();
  return (
    <div className="relative flex-shrink-0">
      <div
        className="w-9 h-9 rounded-full border-2 flex items-center justify-center overflow-hidden bg-[#0A0A0C]"
        style={{ borderColor: `var(--sc-${color})` }}
      >
        {url ? (
          <img
            src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${url}`}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span
            className="sc-heading text-xs font-black"
            style={{ color: `var(--sc-${color})` }}
          >
            {initials}
          </span>
        )}
      </div>
      {live && (
        <span
          className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-[#0A0A0C]"
          style={{ background: "var(--sc-pink)", boxShadow: "0 0 6px var(--sc-pink)" }}
        />
      )}
    </div>
  );
}
